/**
 * Text-size ranges for controls writers can reach (#2030).
 *
 * One shared top. Sidebar, panels, plot, and research keep their own
 * floor, step, default, and storage key. A stored percent inside those
 * ranges loads unchanged.
 *
 * Settings → Appearance → Text Size and the chat stepper share `zoomLevel`.
 * Steps start at 100% (12px UI text stays at least 12px; draft text at
 * 100% is 14px) and move by 15 so each click is visible: 100, 115, 130…
 * An older saved percent snaps to the nearest step.
 *
 * Story Board's discrete steps and the series timeline's canvas zoom are
 * not this module.
 */

export const TEXT_SIZE_MAX = 250;

/** Chat composer stepper and Settings → Appearance → Text Size. */
export const CHAT_TEXT_SIZE = {
  min: 100,
  max: TEXT_SIZE_MAX,
  step: 15,
  default: 100,
  storageKey: 'zoomLevel',
};

/** Sidebar footer stepper. Key stays brainstormy.jetage.sidebarZoom.v2. */
export const SIDEBAR_TEXT_SIZE = {
  min: 70,
  max: TEXT_SIZE_MAX,
  step: 10,
  default: 130,
};

/** Compendium, Bookmarks, and Research header. Key brainstormy.jetage.panelZoom. */
export const PANEL_TEXT_SIZE = {
  min: 90,
  max: TEXT_SIZE_MAX,
  step: 10,
  default: 120,
};

/** Plot window (Plot Dossier). Key brainstormy.plotDossier.zoom. */
export const PLOT_TEXT_SIZE = {
  min: 100,
  max: TEXT_SIZE_MAX,
  step: 10,
  default: 150,
};

/**
 * Research chat's own stepper, plus ZoomSlider / useZoom (Theme Workshop
 * reads that hook and does not render a control).
 */
export const RESEARCH_CHAT_TEXT_SIZE = {
  min: 80,
  max: TEXT_SIZE_MAX,
  step: 10,
  default: 100,
};

export function textSizeSteps(range) {
  const steps = [];
  for (let value = range.min; value <= range.max; value += range.step) {
    steps.push(value);
  }
  return steps;
}

/**
 * Finite stored percent inside [min, max] returns unchanged.
 * Missing, blank, or non-numeric returns the range default.
 * Outside [min, max] returns the range default. Does not snap to a step.
 */
export function readStoredPercent(raw, range) {
  if (raw == null || String(raw).trim() === '') return range.default;
  const parsed = parseInt(String(raw), 10);
  if (!Number.isFinite(parsed)) return range.default;
  if (parsed < range.min || parsed > range.max) return range.default;
  return parsed;
}

/**
 * Nearest step in `range`. A tie goes to the higher step.
 * A value past either end lands on that end.
 */
export function nearestTextSizeStep(value, range) {
  const steps = textSizeSteps(range);
  let best = steps[0];
  let bestDist = Math.abs(value - best);
  for (const step of steps) {
    const dist = Math.abs(value - step);
    if (dist < bestDist || (dist === bestDist && step > best)) {
      best = step;
      bestDist = dist;
    }
  }
  return best;
}

/**
 * Chat and Settings share `zoomLevel`. A finite stored integer snaps to
 * the nearest step, so 75 and 105 open at 100, 110 opens at 115, and
 * 130 stays 130. Missing or non-numeric falls back to 100.
 */
export function readChatZoom(raw) {
  if (raw == null || String(raw).trim() === '') return CHAT_TEXT_SIZE.default;
  const parsed = parseInt(String(raw), 10);
  if (!Number.isFinite(parsed)) return CHAT_TEXT_SIZE.default;
  return nearestTextSizeStep(parsed, CHAT_TEXT_SIZE);
}
